import React, { useEffect } from "react";
import { Drawer, Form, Select, Button, Tag } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import {
  teamNameFormItem,
  descriptionFormItem,
  activeFormItem,
} from "../../components/form/formItems";
import { Application, TeamFormProps } from "./TeamInterfaces";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";

const { Option } = Select;
const TeamForm: React.FC<TeamFormProps> = ({
  initialValues,
  applicationsList,
  membersList,
  hasPermissions,
  buttonLabel,
  form,
  isOpen,
  openDrawer,
  closeDrawer,
  submitting,
  onSubmit,
}) => {
  const [selectedAppId, setSelectedAppId] = React.useState<string | undefined>();
  useDrawerScrollMargin(initialValues?._id, isOpen)

  useEffect(() => {
    if (!isOpen) {
      setSelectedAppId(undefined);
    }

    if (initialValues) {
      const validApp = applicationsList.find(
        app => app._id === initialValues.customerApplication && app.name
      );

      const selectedAppId = validApp ? validApp._id : undefined;

      const validMembers = selectedAppId
        ? (initialValues.members || []).filter(id =>
          membersList.some(
            m =>
              m.value === id &&
              m.title &&
              m.customerApplications?.some((app: Application) => app._id === selectedAppId)
          )
        )
        : [];

      form?.setFieldsValue({
        ...initialValues,
        customerApplication: selectedAppId,
        isActive: initialValues.isActive ?? true,
        members: validMembers
      });

      setSelectedAppId(selectedAppId);
    }
  }, [initialValues, form, membersList, applicationsList, isOpen]);

  const handleSwitchChange = (checked: boolean) => {
    form?.setFieldsValue({ isActive: checked });
  };
  const onPreventMouseDown = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
  };
  const customTagRender = ({ label, closable, onClose }: any) => {
    return (
      <Tag
        color="var(--tertiary-color)"
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3, marginBottom: 3 }}
      >
        {label}
      </Tag>
    );
  };

  const filteredMembersList = React.useMemo(() => {
    return membersList.filter(member =>
      member.customerApplications?.some((app: Application) => app._id === selectedAppId)
    );
  }, [membersList, selectedAppId]);
  return (
    <>
      <Button
        type="primary"
        className="action-btn"
        icon={buttonLabel === "Edit" ? <EditOutlined /> : <PlusOutlined />}
        onClick={openDrawer}
        disabled={!hasPermissions}
      >
        {buttonLabel}
      </Button>

      <Drawer
        title={buttonLabel === "Edit" ? `${buttonLabel} Team` : buttonLabel}
        width={500}
        onClose={closeDrawer}
        open={isOpen}
        className="manage-pages-drawer"
      >
        <Form form={form} layout="vertical" onFinish={onSubmit} autoComplete="off">
          {teamNameFormItem(true)}

          {descriptionFormItem(true)}

          <Form.Item
            name="customerApplication"
            label="Customer Application"
            rules={[{ required: true, message: "Please select an application" }]}
          >
            <Select placeholder="Select application"
              onChange={(value) => {
                setSelectedAppId(value);
                form?.setFieldsValue({ members: [] });
              }}
            >
              {applicationsList
                ?.map((app) => (
                  <Option key={app._id} value={app._id}>
                    {app.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="members"
            label="Members"
            rules={[{ required: true, message: "Please select team members" }]}
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="Select team members"
              options={filteredMembersList}
              fieldNames={{ label: 'title', value: 'value' }}
              filterOption={(inputValue, option) => {
                if (!option || !option.title) return false;
                return option.title.toLowerCase().includes(inputValue.toLowerCase());
              }}
              tagRender={customTagRender}
            />
          </Form.Item>
          {buttonLabel === "Edit" && activeFormItem(handleSwitchChange)}

          <div style={{ textAlign: "right" }}>
            <Button onClick={closeDrawer} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              Submit
            </Button>
          </div>
        </Form>
      </Drawer>
    </>
  );
};

export default TeamForm;